import { Box, Flex, Text } from '@chakra-ui/react';
import { EventType as AnalyticsEventType } from '@waitroom/analytics';
import {
  logout,
  selectCurrentUser,
  selectIsCurrentUserGuest,
  useAuth,
  useHasAccess,
} from '@waitroom/auth';
import { fullName, isFreeOrFreeTrialPlan, setModal } from '@waitroom/common';
import { Role } from '@waitroom/models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { routes } from '../../../../../../constants/routes';
import { analyticsService } from '../../../../../analytics/services';
import { AuthView } from '../../../../../auth/hooks/useAuthFlow';
import { buildImageSrc, imageSizes } from '../../../../../image/utils/helpers';
import { SubscriptionAvatar } from '../../../../../user/components/Avatar/Avatar';
import { ModalType } from '../../../App/Modals/types';
import DropdownMenu from '../../../Menu/Dropdown/DropdownMenu';
import { MenuItem } from '../../../Menu/Menu';
import {
  DashButton,
  HostButton,
  Loader,
  // ProductHuntButton,
  UnstyledButton,
  defaultItems as defItems,
} from './Header.items';

export interface UseComponentProps {
  auth: boolean;
  host: boolean;
  dashboard: boolean;
  defaultItems: boolean;
  menuItems?: MenuItem[];
}
export interface UseComponentResponse {
  items: MenuItem[];
}

export const useComponent = ({
  auth,
  host,
  dashboard,
  defaultItems,
  menuItems,
}: UseComponentProps): UseComponentResponse => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    state: { isAuthenticated, status },
  } = useAuth();
  const { access } = useHasAccess(Role.SESSION_OWNER);
  const isHost = !!access[Role.SESSION_OWNER];
  const isGuest = useSelector(selectIsCurrentUserGuest);
  const currentUser = useSelector(selectCurrentUser);
  const hasUser = !!currentUser;
  const fullname = fullName(currentUser);
  const premium = !isFreeOrFreeTrialPlan(currentUser?.subscriptionPlan);
  const { avatar } = currentUser ?? {};
  const isLoading = !status || status === 'loading';
  const dispatchLogout = useCallback(() => {
    analyticsService.track(AnalyticsEventType.Logout, {});
    analyticsService.logout();
    dispatch(logout());
  }, [dispatch]);

  const items: MenuItem[] = useMemo(() => {
    const itms = [
      ...(menuItems || []),
      ...(defaultItems ? defItems : [defItems[0]]),
      {
        render: () => <Box w={0} order={20} ml="auto !important" />,
        renderModal: null,
        fixed: true,
      },
    ];
    if (isLoading) {
      itms.push({
        render: Loader,
        renderModal: Loader,
      });
      return itms;
    }
    if (auth && isAuthenticated && hasUser) {
      const avatarPic = (
        <SubscriptionAvatar
          src={buildImageSrc(avatar, imageSizes.avatar[0], true)}
          name={fullname}
          premium={premium}
          size="xs"
        />
      );
      itms.push({
        render: () => (
          <Box order={20} py={0}>
            <DropdownMenu
              hover
              placement="bottomRight"
              button={avatarPic}
              items={[
                ...(isGuest
                  ? []
                  : [
                      {
                        to: routes.PROFILE.DEFAULT.link,
                        children: t('global.profile'),
                      },
                    ]),
                {
                  onClick: dispatchLogout,
                  children: t('global.logOut'),
                },
              ]}
            />
          </Box>
        ),
        renderModal: () => (
          <Flex
            {...(isGuest ? {} : { as: Link, to: routes.PROFILE.DEFAULT.route })}
            display="flex"
            className="none"
            direction="row"
            align="center"
            py={5}
            order={20}
          >
            {avatarPic}
            <Text as="div" fontWeight="extrabold" py={1} ml={4} isTruncated>
              {fullname}
            </Text>
          </Flex>
        ),
        fixed: true,
      });
    }
    if (!isAuthenticated && auth) {
      itms.push({
        as: UnstyledButton,
        trans: 'global.logIn',
        onClick: () =>
          setModal({
            type: ModalType.AUTH,
            props: {
              view: AuthView.LOGIN,
            },
          }),
        order: 20,
        px: 2,
        fixed: true,
        breakpoint: 'sm',
        'data-testid': 'menu-btn-login',
      });
    }
    if (dashboard && isHost) {
      itms.push({
        to: routes.DASHBOARD.SESSIONS.link,
        trans: 'global.dashboard',
        render: DashButton,
        order: 21,
      });
    }
    if (host && !isHost && !isGuest) {
      itms.push({
        to: routes.HOST,
        trans: isAuthenticated ? 'header.hostingAuthedButton' : 'header.hostingButton',
        render: HostButton,
        order: 21,
      });
    }
    // itms.push({
    //   render: ProductHuntButton,
    //   renderModal: null,
    //   order: 19,
    // });
    return itms;
  }, [
    auth,
    avatar,
    dashboard,
    defaultItems,
    dispatchLogout,
    fullname,
    hasUser,
    host,
    isAuthenticated,
    isGuest,
    isHost,
    isLoading,
    menuItems,
    premium,
    t,
  ]);
  return { items };
};
